import React, { useMemo } from 'react';

import Button from 'components/partials/button/button';
import Toggle from 'components/partials/toggle/toggle';
import TableComponent from 'components/partials/table-component/table-component';

import { getNonModeledTableSettings } from './non-modeled-table-settings';

import type { UseNonModeledAdjustmentsResult } from '../hooks/use-non-modeled-adjustments';

import Icons from 'assets/icons';

interface NonModeledTabProps {
  showLastYear: boolean;
  onShowLastYearChange: (value: boolean) => void;
  nonModeledAdjustments: UseNonModeledAdjustmentsResult;
  readOnly?: boolean;
}

const NonModeledTab = ({
  showLastYear,
  onShowLastYearChange,
  nonModeledAdjustments,
  readOnly,
}: NonModeledTabProps): React.JSX.Element => {
  const {
    rows,
    allRepackaged,
    someRepackaged,
    totalUsingPreviousAwards,
    isDirtyGlobal,
    isSaving,
    handleEnrollmentChange,
    handleDiscountRateChange,
    handleRepackageToggle,
    handleRepackageToggleAll,
    handleRowUndo,
    handleRevertAll,
    handleSaveDraft,
  } = nonModeledAdjustments;

  const isTableDisabled = readOnly || isSaving;

  const columns = useMemo(
    () =>
      getNonModeledTableSettings({
        onEnrollmentChange: handleEnrollmentChange,
        onDiscountRateChange: handleDiscountRateChange,
        onRepackageToggle: handleRepackageToggle,
        onRepackageToggleAll: handleRepackageToggleAll,
        onRowUndo: handleRowUndo,
        allRepackaged,
        someRepackaged,
        readOnly: isTableDisabled,
      }),
    [
      handleEnrollmentChange,
      handleDiscountRateChange,
      handleRepackageToggle,
      handleRepackageToggleAll,
      handleRowUndo,
      allRepackaged,
      someRepackaged,
      isTableDisabled,
    ]
  );

  return (
    <div className="flex flex-col gap-3" data-testid="results-non-modeled-tab">
      <div className="flex items-center justify-between max-h-7">
        <h3 className="text-lg font-bold text-gray-700">
          Non-modeled aid results
        </h3>
        {isDirtyGlobal && (
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="reserve-outlined"
              size="xs"
              iconBefore={<Icons.Undo aria-hidden="true" />}
              onClick={handleRevertAll}
              disabled={isSaving}
              data-testid="non-modeled-revert-changes"
            >
              Revert changes
            </Button>
            <Button
              type="button"
              variant="reserve-filled"
              size="xs"
              onClick={handleSaveDraft}
              loading={isSaving}
              data-testid="non-modeled-apply-adjustments"
            >
              Apply adjustments
            </Button>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <Toggle
            isToggleOn={showLastYear}
            onToggleChange={onShowLastYearChange}
            toggleText="Last year results"
            size="sm"
            data-testid="results-last-year-toggle"
          />

          {!!totalUsingPreviousAwards && (
            <span
              className="flex items-center gap-1 text-sm font-medium text-gray-600"
              data-testid="non-modeled-total-using-previous-awards"
            >
              <Icons.Info className="size-4 shrink-0" aria-hidden="true" />
              {`Total using previous awards: ${totalUsingPreviousAwards}`}
            </span>
          )}
        </div>

        <div inert={isTableDisabled || undefined}>
          <TableComponent
            columns={columns}
            data={rows}
            isLoading={false}
            pagination={false}
            getRowClassName={(row) => (row.isDirty ? 'bg-navy-50' : '')}
          />
        </div>
      </div>
    </div>
  );
};

export default NonModeledTab;
